import React, { useEffect, useMemo, useRef, useState } from 'react';
import { typography, surfaces, borders, textColors } from '../../../design-system/tokens';
import { SparklesIcon } from '../../navbar/icons';

const DEFAULT_PROMPTS = [
  {
    id: 'below-hero',
    prompt: 'Add a new component right below the hero section to introduce the next most important part of my website.',
  },
  {
    id: 'home-copy',
    prompt: 'Improve the homepage copy so it sounds clearer, more professional, and more specific to my background.',
  },
  {
    id: 'projects-section',
    prompt: 'Create a projects section that highlights my best work with short descriptions and a clean layout.',
  },
];

const DEFAULT_VISIBLE_PROMPT_COUNT = DEFAULT_PROMPTS.length;
const GRID_COLUMN_CLASS_BY_COUNT = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

export const getVisiblePromptCount = (width) => {
  const normalizedWidth = Number(width);
  if (!Number.isFinite(normalizedWidth) || normalizedWidth <= 0) {
    return DEFAULT_VISIBLE_PROMPT_COUNT;
  }
  if (normalizedWidth < 240) {
    return 1;
  }
  if (normalizedWidth < 340) {
    return 2;
  }
  return DEFAULT_VISIBLE_PROMPT_COUNT;
};

const buildPromptPreview = (promptText) => {
  const words = String(promptText || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '...';
  }
  return `${words.slice(0, 2).join(' ')}...`;
};

const renderReviewButton = ({ disabled, keepComposerFocus, label, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onMouseDown={keepComposerFocus}
    onClick={onClick}
    className={`w-full px-3 py-2 rounded-lg border transition-all ${typography.caption} border-green-300 bg-green-400 text-white hover:bg-green-300 dark:border-green-400 dark:bg-green-400 dark:text-white dark:hover:bg-green-300 ${
      disabled ? 'opacity-60 cursor-not-allowed' : ''
    } flex items-center justify-center gap-2 text-center`}
  >
    <SparklesIcon className="w-4 h-4 text-white flex-shrink-0" />
    <span>{label}</span>
  </button>
);

const AIQuickPrompts = ({
  disabled = false,
  isReviewMode = false,
  showStandardPrompts = true,
  onSelectPrompt,
  onActivateReviewMode,
  onDeactivateReviewMode,
}) => {
  const promptCardRef = useRef(null);
  const [visiblePromptCount, setVisiblePromptCount] = useState(DEFAULT_VISIBLE_PROMPT_COUNT);

  const keepComposerFocus = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    const updateVisiblePromptCount = () => {
      if (!promptCardRef.current) return;
      setVisiblePromptCount(getVisiblePromptCount(promptCardRef.current.offsetWidth));
    };

    updateVisiblePromptCount();

    if (typeof ResizeObserver !== 'undefined' && promptCardRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        updateVisiblePromptCount();
      });
      resizeObserver.observe(promptCardRef.current);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener('resize', updateVisiblePromptCount);
    return () => window.removeEventListener('resize', updateVisiblePromptCount);
  }, []);

  const visiblePrompts = useMemo(
    () => DEFAULT_PROMPTS.slice(0, visiblePromptCount),
    [visiblePromptCount]
  );
  const gridColumnClassName = GRID_COLUMN_CLASS_BY_COUNT[visiblePrompts.length] || GRID_COLUMN_CLASS_BY_COUNT[DEFAULT_VISIBLE_PROMPT_COUNT];

  if (isReviewMode) {
    return (
      <div className="px-3 pt-3 pb-1">
        <button
          type="button"
          disabled={disabled}
          onMouseDown={keepComposerFocus}
          onClick={() => onDeactivateReviewMode?.()}
          className={`w-full px-3 py-2 rounded-lg border transition-all ${typography.caption} border-gray-200 dark:border-dark-700 bg-gray-100 dark:bg-dark-800 text-green-400 hover:bg-gray-200 dark:hover:bg-dark-700 ${
            disabled ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        >
          End AI Review
        </button>
      </div>
    );
  }

  if (!showStandardPrompts) {
    return (
      <div className="px-3 pt-3 pb-1">
        {renderReviewButton({
          disabled,
          keepComposerFocus,
          label: 'AI Review my website',
          onClick: () => onActivateReviewMode?.(),
        })}
      </div>
    );
  }

  return (
    <div className="px-3 pt-3 pb-1">
      <div
        ref={promptCardRef}
        data-testid="ai-quick-prompts"
        className={`rounded-xl border ${borders.default} bg-sky-50/80 dark:bg-dark-900/50 p-3`}
      >
        <div className="flex flex-col gap-2">
          <p className={`${typography.caption} ${textColors.secondary}`}>AI Suggestions</p>
          {showStandardPrompts ? (
            <div data-testid="ai-standard-prompts-row" className={`grid ${gridColumnClassName} gap-2`}>
              {visiblePrompts.map((prompt) => {
                const promptPreview = buildPromptPreview(prompt.prompt);
                return (
                  <button
                    key={prompt.id}
                    type="button"
                    disabled={disabled}
                    aria-label={prompt.prompt}
                    title={prompt.prompt}
                    onMouseDown={keepComposerFocus}
                    onClick={() => onSelectPrompt?.(prompt.prompt)}
                    className={`w-full min-w-0 px-2.5 py-1.5 rounded-lg border text-center whitespace-nowrap leading-4 transition-colors ${borders.default} ${surfaces.tertiary} ${surfaces.hover} ${textColors.primary} ${typography.caption} ${
                      disabled ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    <span aria-hidden="true">{promptPreview}</span>
                  </button>
                );
              })}
            </div>
          ) : null}
          <div data-testid="ai-review-prompt-row">
            {renderReviewButton({
              disabled,
              keepComposerFocus,
              label: 'AI Review my website',
              onClick: () => onActivateReviewMode?.(),
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIQuickPrompts;
